import React, { useState, useEffect } from 'react';
import '../styles/Fundraising.css';

interface FundraisingData {
  balance: number;
  blockHeight: number;
  targetAmount: number;
  percentComplete: number;
  address: string;
}

const Fundraising: React.FC = () => {
  const [fundraisingData, setFundraisingData] = useState<FundraisingData>({
    balance: 0,
    blockHeight: 0,
    targetAmount: 777777,
    percentComplete: 0,
    address: 'RAUk9TY9FhcD3h99twffFcf4gtaEL7F78R'
  });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch balance data
        const balanceResponse = await fetch('https://kmdexplorer.io/insight-api-komodo/addr/RAUk9TY9FhcD3h99twffFcf4gtaEL7F78R/balance');
        const balanceData = await balanceResponse.text();
        const balanceInSatoshis = parseInt(balanceData);
        const balanceInKMD = balanceInSatoshis / 100000000;
        
        // Fetch block height data
        const syncResponse = await fetch('https://kmdexplorer.io/insight-api-komodo/sync');
        const syncData = await syncResponse.json();
        const blockHeight = syncData.blockChainHeight;
        
        // Calculate percentage complete
        const percentComplete = (balanceInKMD / fundraisingData.targetAmount) * 100;
        
        setFundraisingData({
          ...fundraisingData,
          balance: balanceInKMD,
          blockHeight: blockHeight,
          percentComplete: percentComplete
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching fundraising data:', err);
        setError('Failed to load fundraising data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
    
    // Set up interval to refresh data every 60 seconds
    const intervalId = setInterval(fetchData, 60000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section id="donations" className="section fundraising-section">
      <div className="container">
        <div className="section-header">
          <h2>Community Support and Donations</h2>
          <p>Join us in reviving the essence of Komodo by contributing to our community-driven initiative</p>
        </div>
        
        <div className="fundraising-container">
          <div className="fundraising-info">
            <h3>Help Us Reach Our Goal</h3>
            <p>
              As a community project, we rely on the support and participation of the Komodo community. 
              Your contributions will help us develop and implement the features that will make Komodo Classic a reality.
            </p>
            <p>
              You can use the donation address for voluntary donations to our development team. 
              There is no limit on donation amounts, and all funds collected will be used for project development. 
              You can use the donation address to send both <a href="https://kmdexplorer.io/address/RAUk9TY9FhcD3h99twffFcf4gtaEL7F78R" target="_blank" rel="noopener noreferrer">KMD</a> and <a href="https://explorer.kmdclassic.com/address/RAUk9TY9FhcD3h99twffFcf4gtaEL7F78R" target="_blank" rel="noopener noreferrer">KMDCL</a> coins.
            </p>
            
            <div className="donation-address">
              <h4>Donation Address:</h4>
              <div className="address-container">
                <code>{fundraisingData.address}</code>
                <button className="copy-button" onClick={() => navigator.clipboard.writeText(fundraisingData.address)}>
                  Copy
                </button>
              </div>
            </div>
          </div>
          
          <div className="fundraising-stats">
            {loading ? (
              <div className="loading-spinner">Loading...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <>
                <div className="progress-circle-container">
                  <div className="progress-circle">
                    <svg viewBox="0 0 36 36" className="circular-chart">
                      <path 
                        className="circle-bg"
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path 
                        className="circle"
                        strokeDasharray={`${fundraisingData.percentComplete}, 100`}
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <text x="18" y="20.35" className="percentage">
                        {fundraisingData.percentComplete.toFixed(2)}%
                      </text>
                    </svg>
                  </div>
                  
                  <div className="stats-details">
                    <div className="stat-item">
                      <span className="stat-label">Raised:</span>
                      <span className="stat-value">{fundraisingData.balance.toFixed(8)} KMD</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Target:</span>
                      <span className="stat-value">{fundraisingData.targetAmount.toLocaleString(undefined, { useGrouping: false })} KMD</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Current Block:</span>
                      <span className="stat-value">{fundraisingData.blockHeight.toLocaleString(undefined, { useGrouping: false })}</span>
                    </div>
                  </div>
                </div>
                
                <div className="linear-progress">
                  <div 
                    className="linear-progress-bar" 
                    style={{ width: `${Math.min(fundraisingData.percentComplete, 100)}%` }}
                  ></div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Fundraising;
